/**
 * Which Super Admin screen a platform operator may open, from the permission its PRIMARY read
 * (the reads it sends on load, default tab) needs on the server: the route decorators,
 * `require_perm(...)` or `require_super_admin`. Audited route by route on 2026-10-07 (S8).
 *
 * One table serves the sidebar (an entry the operator cannot open is not shown) and the route
 * gate in SuperAdminLayout (a direct visit to such a screen shows the no-access panel). A screen
 * that also makes secondary reads the role may lack handles that 403 itself (NoAccess.jsx).
 *
 * A requirement is one of:
 *   { superAdmin: true }   the route uses `require_super_admin`, which ignores permissions
 *   { perm: "x.read" }     `require_perm("x.read")`
 *   { anyOf: [req, ...] }  a screen of tabs, each its own read: it opens when the operator
 *                          meets one of them (requirements shaped like screenWrites.js's)
 *
 * Entries are matched in order; `end: true` matches the exact path only, otherwise the path and
 * everything under it. A path no entry names is not gated here (the router answers it).
 */

import { useOperator } from "./OperatorContext";
import { meets } from "./screenWrites";

const BASE = "/super-admin";

/**
 * Global Data's tabs, one read each: `GET /super-admin/global/{tab}` checks the tab's own
 * `TabSpec.permission` (backend/modules/global_data/tabs.py) for every caller, a super admin
 * included. The page offers only the tabs the operator meets, so no role is shown a tab that
 * can only answer 403.
 */
export const GLOBAL_DATA_TAB_READS = {
  customers: { perms: ["data.crm.read"] },
  bookings: { perms: ["data.bookings.read"] },
  programs: { perms: ["data.programs.read"] },
  promos: { perms: ["data.promos.read"] },
  giftcards: { perms: ["data.giftcards.read"] },
  media: { perms: ["data.media.read"] },
  content: { perms: ["data.content.read"] },
  blog: { perms: ["data.content.read"] },
  billing: { perms: ["finance.read"] },
  invoices: { perms: ["finance.read"] },
  branding: { perms: ["data.branding.read"] },
  insights: { perms: ["data.insights.read"] },
};

/** Whether `operator` may read the Global Data tab `key`; a tab the table does not name is never offered. */
export const mayReadGlobalTab = (key, operator) => meets(GLOBAL_DATA_TAB_READS[key], operator);

export const SCREENS = [
  // Overview: GET /stats and GET /finance are require_super_admin; its 30-day series is the
  // secondary read `financeSeries` below.
  { path: BASE, end: true, superAdmin: true },
  // The tenants list (GET /tenants) is super admin only; one practice's 360 view (GET /tenants/{id}/360) is tenants.read.
  { path: `${BASE}/tenants`, end: true, superAdmin: true },
  { path: `${BASE}/tenants`, perm: "tenants.read" },
  { path: `${BASE}/reviews`, perm: "tenants.read" },
  // GET /custom-domains is tenants.read (the policy rides along in that response).
  { path: `${BASE}/custom-domains`, perm: "tenants.read" },
  { path: `${BASE}/plans`, superAdmin: true },
  { path: `${BASE}/plan-features`, superAdmin: true },
  { path: `${BASE}/catalog`, superAdmin: true },
  { path: `${BASE}/team`, perm: "team.read" },
  // Finance: GET /finance is require_super_admin; its period section is the secondary read `financeSeries`.
  { path: `${BASE}/finance`, superAdmin: true },
  { path: `${BASE}/revenue`, superAdmin: true },
  { path: `${BASE}/platform-revenue`, superAdmin: true },
  { path: `${BASE}/transactions`, superAdmin: true },
  { path: `${BASE}/client-transactions`, perm: "finance.read" },
  { path: `${BASE}/gateway`, perm: "finance.read" },
  { path: `${BASE}/affiliate`, perm: "finance.read" },
  { path: `${BASE}/payments`, perm: "settings.read" },
  { path: `${BASE}/hq/customers`, perm: "data.crm.read" },
  { path: `${BASE}/hq/finance`, perm: "finance.read" },
  { path: `${BASE}/hq/service-leads`, perm: "data.crm.read" },
  { path: `${BASE}/hq/social`, perm: "tenants.read" },
  // The Promos tab loads first; the Gifts tab (data.giftcards.read) is held by the same roles.
  { path: `${BASE}/hq/gift-promo`, perm: "data.promos.read" },
  { path: `${BASE}/hq/ai`, perm: "data.insights.read" },
  // Lifecycle, the default tab, reads settings.read (Custom rules reads tenants.read).
  { path: `${BASE}/hq/automation`, perm: "settings.read" },
  { path: `${BASE}/hq/blog`, perm: "data.content.read" },
  { path: `${BASE}/hq/site`, perm: "data.content.read" },
  // GET /settings is super admin only; the page reads only that endpoint.
  { path: `${BASE}/settings`, superAdmin: true },
  { path: `${BASE}/global/learn-telehealth`, superAdmin: true },
  // Each Global Data tab checks its own permission: the screen opens for anyone who can read one.
  { path: `${BASE}/global`, anyOf: Object.values(GLOBAL_DATA_TAB_READS) },
  { path: `${BASE}/audit`, perm: "audit.read" },
  { path: `${BASE}/activity`, superAdmin: true },
  { path: `${BASE}/integrations`, perm: "settings.read" },
  { path: `${BASE}/webhooks`, perm: "audit.read" },
  { path: `${BASE}/platform-credentials`, superAdmin: true },
];

const matches = (screen, pathname) => {
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  if (screen.end) return path === screen.path;
  return path === screen.path || path.startsWith(`${screen.path}/`);
};

/** The table entry that governs `pathname`, `null` when none does. */
export const screenFor = (pathname) => SCREENS.find((s) => matches(s, pathname || "")) || null;

/**
 * Whether the operator (`useOperator()`'s `{ isSuperAdmin, can }`) may open `pathname`.
 * A super admin may open everything.
 */
export function mayOpen(pathname, operator) {
  if (operator.isSuperAdmin) return true;
  const screen = screenFor(pathname);
  if (!screen) return true;
  if (screen.superAdmin) return false;
  if (screen.anyOf) return screen.anyOf.some((requirement) => meets(requirement, operator));
  return operator.can(screen.perm);
}

/**
 * Reads a screen makes besides its primary one that some of the roles who open the screen do not
 * hold. A screen asks `useSecondaryRead(name)` before sending such a read, so it never fires a
 * request the role is refused; the part of the screen it feeds is left out instead. Requirements
 * have the shape of screenWrites.js's.
 */
export const SECONDARY_READS = {
  // /super-admin/hq/site: the media library (GET /hq/site/media, its usage) is data.media.read,
  // which Support lacks although it opens Site (data.content.read).
  siteMedia: { perms: ["data.media.read"] },
  // /super-admin/tenants/{id}: the financial history (GET /tenants/{id}/billing/credits and
  // GET /tenants/{id}/billing/refunds) is finance.read, which Support lacks although it opens the
  // 360 view (tenants.read). The refund picker's own read (billing/refundable-transactions) is
  // billing.refund, the permission of the write it serves, so it is gated by screenWrites.js.
  tenantCredits: { perms: ["finance.read"] },
  // /super-admin (Overview) and /super-admin/finance: the daily series (GET /finance/timeseries)
  // is finance.read. Both screens are super admin only, and a super admin does NOT always hold
  // it: a permissions override can revoke it. The figures it feeds are left out for them. (An
  // env super admin the team lists under another role is not a super admin at all: the team
  // role decides, `rbac.is_super_admin_operator`.)
  financeSeries: { perms: ["finance.read"] },
  // The practice picker (GET /tenants/options) is super admin only. The automation dry run
  // offers it; an operator holding settings.write through an override runs without a practice.
  tenantOptions: { superAdmin: true },
};

/** Whether the signed-in operator may send the secondary read `name`. Only valid under `<OperatorProvider>`. */
export function useSecondaryRead(name) {
  const operator = useOperator();
  const requirement = SECONDARY_READS[name];
  if (!requirement) throw new Error(`useSecondaryRead(): no SECONDARY_READS entry named ${JSON.stringify(name)}`);
  return meets(requirement, operator);
}
